/* eslint-disable */
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Body2, Card, ConfirmDialog, Trigger } from "@activecollab/components";
import PropTypes from "prop-types";
import { showErrorToast } from "~/components/Toast";
import { useDeleteApiToken } from "~/repository";
import ApiTokenAddDialog from "./ApiTokenAddDialog";
import ApiTokenDialog from "./ApiTokenDialog";
import ApiTokensItem from "./ApiTokensItem";

const ApiTokensList = ({ data, userId }) => {
  const { t } = useTranslation();
  const [deleteElement, setDeleteElement] = useState(null);
  const deleteApiTokenMutation = useDeleteApiToken();
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [openAddTokenDialog, setOpenAddTokenDialog] = useState(false);

  const handleOpenAddTokenDialog = useCallback(() => {
    setOpenAddTokenDialog(true);
  }, []);

  const handleCloseAddTokenDialog = useCallback(() => {
    setOpenAddTokenDialog(false);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteElement(null);
  }, []);

  const handleDeleteToken = useCallback(
    (id) => {
      deleteApiTokenMutation.mutate(
        { userId: userId, tokenId: id },
        {
          onSuccess: () => {
            handleCloseDeleteDialog();
          },
          onError: () => {
            showErrorToast(t("Unable to delete API token"));
          },
        }
      );
    },
    [deleteApiTokenMutation, handleCloseDeleteDialog, t, userId]
  );

  return (
    <>
      {data?.length ? (
        <>
          <Card className="tw-shadow-none tw-min-h-[48px] tw-flex tw-flex-col tw-rounded-b-none tw-rounded-t-none tw-overflow-x-auto">
            <div className="tw-min-w-[850px]">
              <div className="tw-flex tw-items-center tw-pl-5 tw-pr-4 tw-min-h-[48px] tw-gap-6 ">
                <Body2
                  className="tw-w-[250px] tw-shrink-0"
                  weight="bold"
                >
                  {t("Name and Vendor")}
                </Body2>
                <Body2
                  className="tw-w-[100px] tw-shrink-0 tw-truncate"
                  weight="bold"
                >
                  {t("Last Used")}
                </Body2>
                <Body2
                  className="tw-w-[196px]"
                  weight="bold"
                >
                  {t("Token")}
                </Body2>
                <Body2
                  className="tw-w-[140px] tw-shrink-0"
                  weight="bold"
                >
                  {t("Access")}
                </Body2>
              </div>
              {data?.map((item) => {
                return (
                  <ApiTokensItem
                    key={`token-${item.id}`}
                    item={item}
                    setDeleteElement={setDeleteElement}
                    setSelectedTokenId={setSelectedTokenId}
                    userId={userId}
                  />
                );
              })}
            </div>
          </Card>

          {deleteElement?.id ? (
            <ConfirmDialog
              open={!!deleteElement.id}
              dialogTitle={t("Delete Token")}
              dialogContent={t(
                `Token will be permanently deleted, and {{client_name}} will no longer be able to access ActiveCollab.`,
                { client_name: deleteElement.name }
              )}
              confirmBtnText={t("Delete")}
              onCancel={handleCloseDeleteDialog}
              onConfirm={() => handleDeleteToken(deleteElement.id)}
              isLoading={deleteApiTokenMutation.isLoading}
            />
          ) : null}
          {selectedTokenId ? (
            <ApiTokenDialog
              onClose={() => setSelectedTokenId(null)}
              userId={userId}
              tokenId={selectedTokenId}
            />
          ) : null}
        </>
      ) : (
        <Card className="tw-shadow-none tw-min-h-[48px] tw-flex tw-items-center tw-pl-5 tw-pr-4 tw-rounded-b-none tw-rounded-t-none tw-border-0 tw-border-t tw-border-solid tw-border-theme-400">
          <Body2 color="tertiary"> {t("No tokens found")}</Body2>
        </Card>
      )}
      <Trigger
        className="tw-w-full"
        onClick={handleOpenAddTokenDialog}
      >
        <Card
          className="tw-border-0 tw-border-t tw-border-solid tw-border-theme-400 tw-min-h-[48px] tw-flex tw-items-center tw-justify-between tw-pl-5 tw-pr-4 tw-py-3 tw-rounded-t-none"
          variant="paper-2"
        >
          <Body2 color="theme-primary">{t("Add Token")}</Body2>
        </Card>
      </Trigger>
      {openAddTokenDialog ? (
        <ApiTokenAddDialog
          open={openAddTokenDialog}
          onClose={handleCloseAddTokenDialog}
          userId={userId}
          setSelectedTokenId={setSelectedTokenId}
        />
      ) : null}
    </>
  );
};

ApiTokensList.propTypes = {
  data: PropTypes.array,
  userId: PropTypes.number,
};

export default ApiTokensList;
